import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Eye, Star, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

function SortHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  const Icon = !isActive ? ArrowUpDown : currentSort.order === "asc" ? ArrowUp : ArrowDown;
  
  return (
    <button 
      className="flex items-center gap-1 hover:text-slate-900 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      {label}
      <Icon className={`w-3 h-3 ${isActive ? "text-blue-600" : ""}`} />
    </button>
  );
}

export default function ReviewsPage() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState({ key: "date", order: "desc" });

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await nestApi.get(`/reviews`, {
        params: {
          page,
          sort_by: sort.key,
          sort_order: sort.order
        }
      });
      setReviews(data.reviews || []);
      setAvgRating(data.average_rating || 0);
      setTotalReviews(data.total_reviews || data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      toast.error("Failed to load reviews");
      setReviews([]);
    }
  }, [page, sort]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      order: prev.key === key && prev.order === "desc" ? "asc" : "desc"
    }));
  };

  return (
    <div className="animate-slideUp space-y-6" data-testid="reviews-page">
      <p className="text-sm text-slate-500">{t("reviews.description")}</p>

      {/* Average Rating Card */}
      <div
        className="bg-white border border-slate-100 rounded-xl shadow-sm p-6"
        data-testid="avg-rating-display"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div>
            <span className="text-sm font-medium text-slate-500">
              {t("reviews.averageRating")}
            </span>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-4xl font-bold text-slate-900">{avgRating}</span>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-5 h-5 ${
                      s <= Math.round(avgRating)
                        ? "text-amber-400 fill-amber-400"
                        : "text-slate-200"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="sm:border-l sm:border-slate-200 sm:pl-6">
            <span className="text-sm font-medium text-slate-500">Total Reviews</span>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-reviews-count">{totalReviews}</p>
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <SortHeader label={t("table.city")} sortKey="city" currentSort={sort} onSort={handleSort} />
              </TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <SortHeader label={t("table.date")} sortKey="date" currentSort={sort} onSort={handleSort} />
              </TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.time")}</TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <SortHeader label={t("table.language")} sortKey="language" currentSort={sort} onSort={handleSort} />
              </TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <SortHeader label={t("common.rating")} sortKey="rating" currentSort={sort} onSort={handleSort} />
              </TableHead>
              <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("common.review")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((r, i) => (
              <TableRow key={r.id} className="table-row-hover" data-testid={`review-row-${r.id}`}>
                <TableCell className="px-4 py-4 text-sm text-slate-600">
                  {(page - 1) * 8 + i + 1}
                </TableCell>
                <TableCell className="px-4 py-4 text-sm font-medium text-slate-900">
                  {r.city}
                </TableCell>
                <TableCell className="px-4 py-4 text-sm text-slate-600">{r.date}</TableCell>
                <TableCell className="px-4 py-4 text-sm text-slate-600">{r.time}</TableCell>
                <TableCell className="px-4 py-4 text-sm text-slate-600">{r.language}</TableCell>
                <TableCell className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-semibold text-slate-900">{r.rating}</span>
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  </div>
                </TableCell>
                <TableCell className="px-4 py-4">
                  <button
                    onClick={() => setSelected(r)}
                    className="text-slate-400 hover:text-blue-600 transition-colors p-1 rounded hover:bg-slate-100"
                    data-testid={`view-review-${r.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
              data-testid="reviews-prev-page"
            >
              <ChevronLeft className="w-4 h-4" /> {t("common.previous")}
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    page === i + 1
                      ? "bg-[#1A1B2E] text-white"
                      : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
              data-testid="reviews-next-page"
            >
              {t("common.next")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Review Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="review-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {selected.city} | {selected.tour_duration} | {selected.date} | {selected.time}
                </DialogTitle>
                <DialogDescription className="sr-only">Review details</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <span className="text-xs uppercase tracking-wider text-slate-400">{t("common.rating")}</span>
                  <div className="flex items-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-6 h-6 ${
                          s <= selected.rating
                            ? "text-amber-400 fill-amber-400"
                            : "text-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider text-slate-400">{t("common.review")}</span>
                  <p className="text-sm text-slate-700 mt-2 leading-relaxed bg-slate-50 p-4 rounded-lg">
                    {selected.text}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
